import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Assignment as RequestIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import { vehicleRequestAPI } from '../services/api';
import type { VehicleRequest } from '../types';

interface TimelineStep {
  key: string;
  label: string;
  completed: boolean;
  date?: string;
  details: string;
  comments?: string;
  icon: string;
  vehicle?: {
    plateNumber: string;
    make: string;
    model: string;
  };
  driver?: string;
  isRejection?: boolean;
}

const RequestStatusTracking: React.FC<{ requestId?: string }> = ({ requestId }) => {
  const [myRequests, setMyRequests] = useState<VehicleRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<VehicleRequest | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { notifications } = useNotifications();

  useEffect(() => {
    loadMyRequests();
  }, [refreshTrigger]);

  useEffect(() => {
    const requestNotifications = notifications.filter(n => 
      n.category === 'system' && n.message.toLowerCase().includes('request')
    );
    
    if (requestNotifications.length > 0) {
      setRefreshTrigger(prev => prev + 1);
    }
  }, [notifications]);

  const loadMyRequests = async () => {
    try {
      setLoading(true);
      const requests = await vehicleRequestAPI.getMyRequests();
      requests.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setMyRequests(requests);
      
      if (requestId) {
        const targetRequest = requests.find(r => r._id === requestId);
        if (targetRequest) {
          setSelectedRequest(targetRequest);
          setDetailDialog(true);
        }
      }
    } catch (error) {
      console.error('Error loading my requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'supervisor_approved': return 'info';
      case 'fleet_approved': return 'success';
      case 'completed': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending Approval';
      case 'supervisor_approved': return 'Pending Fleet';
      case 'fleet_approved': return 'Approved';
      case 'completed': return 'Completed';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const getProgressPercentage = (status: string) => {
    switch (status) {
      case 'pending': return 25;
      case 'supervisor_approved': return 50;
      case 'fleet_approved': return 75;
      case 'completed': return 100;
      case 'rejected': return 100;
      default: return 0;
    }
  };

  const formatDateTime = (dateInput: string | Date | null | undefined) => {
    if (!dateInput) return { date: 'N/A', time: '' };
    const date = new Date(dateInput);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const renderRequestTimeline = (request: VehicleRequest) => {
    const steps: TimelineStep[] = [
      {
        key: 'submitted',
        label: 'Request Submitted',
        completed: true,
        date: request.createdAt?.toString(),
        details: `Submitted by ${request.requestedBy?.firstName || 'Unknown'} ${request.requestedBy?.lastName || ''}`,
        icon: '📝'
      },
      {
        key: 'supervisor',
        label: 'Supervisor Approval',
        completed: request.status !== 'pending',
        date: request.approvedAt?.toString(),
        details: request.approvedBy ? `Approved by ${request.approvedBy.firstName} ${request.approvedBy.lastName}` : 'Pending supervisor review',
        icon: '👤'
      },
      {
        key: 'fleet',
        label: 'Vehicle Assignment',
        completed: request.assignedVehicle !== undefined,
        details: request.assignedVehicle ? `Vehicle: ${request.assignedVehicle.make} ${request.assignedVehicle.model}` : 'Pending assignment',
        vehicle: request.assignedVehicle,
        icon: '🚗'
      },
      {
        key: 'trip',
        label: 'Trip Execution',
        completed: request.status === 'completed',
        details: request.status === 'completed' ? 'Trip completed' : 'Awaiting',
        icon: request.status === 'completed' ? '✅' : '▶️'
      }
    ];

    if (request.status === 'rejected') {
      steps.splice(2, 2, {
        key: 'rejected',
        label: 'Rejected',
        completed: true,
        date: request.updatedAt?.toString(),
        details: request.rejectionReason || 'Request rejected',
        isRejection: true,
        icon: '❌'
      });
    }

    return (
      <Box sx={{ mt: 2 }}>
        {steps.map((step, index) => (
          <Paper key={step.key} sx={{ p: 2, mb: 2, position: 'relative' }}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: step.completed ? (step.isRejection ? 'error.main' : 'success.main') : 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                {step.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">{step.label}</Typography>
                <Typography variant="body2" color="text.secondary">{step.details}</Typography>
                {step.date && <Typography variant="caption" color="text.secondary">{formatDateTime(step.date).date} at {formatDateTime(step.date).time}</Typography>}
                {step.vehicle && <Box sx={{ mt: 1 }}><Chip size="small" label={`${step.vehicle.make} ${step.vehicle.model} (${step.vehicle.plateNumber})`} variant="outlined" /></Box>}
              </Box>
            </Stack>
            {index < steps.length - 1 && <Box sx={{ position: 'absolute', left: 38, top: 50, width: 2, height: 20, bgcolor: 'grey.300' }} />}
          </Paper>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">My Request Status</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setRefreshTrigger(prev => prev + 1)} disabled={loading}>Refresh</Button>
      </Stack>

      {loading && <Box sx={{ p: 4, textAlign: 'center' }}><Typography>Loading...</Typography></Box>}

      {!loading && myRequests.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RequestIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No Requests Found</Typography>
        </Paper>
      )}

      {!loading && myRequests.length > 0 && (
        <Stack spacing={2}>
          {myRequests.map((request) => (
            <Card key={request._id}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                      <Typography variant="h6" noWrap>{request.purpose}</Typography>
                      <Chip size="small" color={getStatusColor(request.status)} label={getStatusText(request.status)} />
                    </Stack>
                    <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">{formatDateTime(request.startDateTime).date}</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" noWrap>{request.destination}</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <GroupIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">{request.passengerCount} passengers</Typography>
                      </Stack>
                    </Stack>
                    <LinearProgress variant="determinate" value={getProgressPercentage(request.status)} sx={{ height: 6, borderRadius: 3 }} color={request.status === 'rejected' ? 'error' : 'primary'} />
                  </Box>
                  <IconButton onClick={() => { setSelectedRequest(request); setDetailDialog(true); }}>
                    <ViewIcon />
                  </IconButton>
                </Stack>
                {request.assignedVehicle && <Alert severity="info" sx={{ mt: 2 }}><Typography variant="body2"><strong>Vehicle:</strong> {request.assignedVehicle.make} {request.assignedVehicle.model} ({request.assignedVehicle.plateNumber})</Typography></Alert>}
                {request.status === 'rejected' && request.rejectionReason && <Alert severity="error" sx={{ mt: 2 }}><Typography variant="body2"><strong>Reason:</strong> {request.rejectionReason}</Typography></Alert>}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Request Details</Typography>
            <Chip color={getStatusColor(selectedRequest?.status || '')} label={getStatusText(selectedRequest?.status || '')} />
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 1 }}>
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>Request Details</Typography>
                <Stack spacing={2}>
                  <Box><Typography variant="body2" color="text.secondary">Purpose</Typography><Typography>{selectedRequest.purpose}</Typography></Box>
                  <Box><Typography variant="body2" color="text.secondary">Destination</Typography><Typography>{selectedRequest.destination}</Typography></Box>
                  <Box><Typography variant="body2" color="text.secondary">Date</Typography><Typography>{formatDateTime(selectedRequest.startDateTime).date} at {formatDateTime(selectedRequest.startDateTime).time}</Typography></Box>
                  <Box><Typography variant="body2" color="text.secondary">Passengers</Typography><Typography>{selectedRequest.passengerCount} people</Typography></Box>
                </Stack>
              </Paper>
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Progress</Typography>
                  <Typography variant="body2" color="text.secondary">{getProgressPercentage(selectedRequest.status)}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={getProgressPercentage(selectedRequest.status)} sx={{ height: 8, borderRadius: 4 }} color={selectedRequest.status === 'rejected' ? 'error' : 'primary'} />
              </Box>
              {renderRequestTimeline(selectedRequest)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestStatusTracking;